/**
 * useDomainAccess — quem enxerga a venda de domínio próprio.
 *
 * ⚠️ **Quem decide é o servidor, não o build.** Até 09/09/2026 o gate era `VITE_FEATURE_DOMAIN_SALES`,
 * uma flag de build igual para todo mundo: ou a feature existia para a base inteira, ou para
 * ninguém. Não dava para ligar de pouco em pouco, que era a intenção desde o começo.
 *
 * Agora `GET /v1/domains/status` responde **204 quando a feature não existe para este usuário** —
 * seja porque a venda está desligada globalmente, seja porque a conta ainda não foi ativada. E a
 * ativação de uma conta é ter sugestão gravada, o que só acontece quando alguém aprova nomes no
 * sugestor local. Aprovar vira, sozinho, a chave que liga menu, rota e cards daquele cliente.
 *
 * O front trata os dois motivos igual de propósito: para quem não tem acesso, são a mesma coisa.
 *
 * A flag de build continua existindo, mas mudou de papel: virou **kill-switch** (desliga para
 * todos e nem chega a bater no servidor), não mais gate por usuário.
 *
 * Chave compartilhada com a tela de Páginas (`usePagesScreen`): o React Query deduplica, então
 * menu, rota e Páginas custam UMA requisição.
 */

import { useQuery } from '@tanstack/react-query';
import domainsService from '../services/domainsService';
import { isDomainSalesEnabled } from '../utils/environment';

export const DOMAIN_STATUS_QUERY = {
  queryKey: ['domains', 'status'],
  queryFn: () => domainsService.getStatus(),
  staleTime: 60_000,
  retry: false,
  refetchOnWindowFocus: false,
  // Silencioso: falha aqui não pode derrubar o menu nem a tela de Páginas. Sem resposta, o
  // tratamento é o mesmo de "não liberado" — esconder. Errar para o lado de mostrar uma tela
  // que o servidor vai recusar seria pior.
  throwOnError: false,
};

/**
 * @returns {{ liberado: boolean, carregando: boolean, status: object|null }}
 *   `liberado` só é true depois da resposta. Enquanto carrega é false — item de menu que aparece
 *   e some é pior que item que demora um instante a aparecer.
 */
export function useDomainAccess() {
  const killSwitch = isDomainSalesEnabled();

  const { data, isPending, isError } = useQuery({
    ...DOMAIN_STATUS_QUERY,
    enabled: killSwitch,
  });

  // Kill-switch desligado: não há requisição em curso, então também não há espera.
  if (!killSwitch) return { liberado: false, carregando: false, status: null };

  return {
    // `null` é o 204 traduzido pelo domainsService — não é ausência de dado, é "não é para você".
    liberado: !isPending && !isError && data != null,
    carregando: isPending,
    status: data ?? null,
  };
}

export default useDomainAccess;
